var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserRepository, CommunityRepository, PostTargetType, FileType } from '@amityco/js-sdk';
import { Box } from '@noom/wax-component-library';
import { info } from '../../../../core/components/Confirm';
import { useAsyncCallback } from '../../../../core/hooks/useAsyncCallback';
import useImage from '../../../../core/hooks/useImage';
import { useActionEvents } from '../../../../core/providers/ActionProvider';
import { isEmpty } from '../../../../helpers';
import withSDK from '../../../../core/hocs/withSDK';
import useUser from '../../../../core/hooks/useUser';
import useLiveObject from '../../../../core/hooks/useLiveObject';
import useErrorNotification from '../../../../core/hooks/useErrorNotification';
import { notification } from '../../../../core/components/Notification';
import { useNavigation } from '../../../../social/providers/NavigationProvider';
import { extractMetadata, formatMentionees } from '../../../../helpers/utils';
import { FileLoaderContainer } from '../../../../core/components/Uploaders/Loader';
import PollModal from '../../../../social/components/post/PollComposer/PollModal';
import PostTargetSelector from './components/PostTargetSelector';
import UploaderButtons from './components/UploaderButtons';
import ImagesUploaded from './components/ImagesUploaded';
import VideosUploaded from './components/VideosUploaded';
import FilesUploaded from './components/FilesUploaded';
import { createPost, showPostCreatedNotification } from './utils';
import { Avatar, PostCreatorContainer, Footer, PostContainer, PostButton, UploadsContainer, PollButton, PollIcon, } from './styles';
import { MAXIMUM_POST_CHARACTERS, MAXIMUM_POST_MENTIONEES } from './constants';
import promisify from '../../../../helpers/promisify';
import Editor from '../../../../core/components/RichTextEditor';
const communityFetcher = (id) => () => CommunityRepository.communityForId(id);
const userFetcher = (id) => () => UserRepository.getUser(id);
const MAX_FILES_PER_POST = 10;
const overCharacterModal = () => info({
    title: React.createElement(FormattedMessage, { id: "postCreator.unableToPost" }),
    content: React.createElement(FormattedMessage, { id: "postCreator.overCharacter" }),
    okText: React.createElement(FormattedMessage, { id: "postCreator.done" }),
    type: 'info',
});
function PostTargetInfo({ postTarget }) {
    var _a;
    return (React.createElement(Box, { color: "gray.500", fontSize: "xs", w: "100%" },
        React.createElement(FormattedMessage, { id: "postCreator.postTarget", values: { postTarget: (_a = React.createElement("b", null, postTarget)) !== null && _a !== void 0 ? _a : '...' } })));
}
const PostCreatorBar = ({ className = '', currentUserId, connected, // connection status
targetType, targetId, canTargetUser, enablePostTargetPicker, communities = [], hasMoreCommunities, loadMoreCommunities, onCreateSuccess = () => { }, maxFiles = MAX_FILES_PER_POST, }) => {
    var _a, _b;
    const { formatMessage } = useIntl();
    const { setNavigationBlocker } = useNavigation();
    const { user } = useUser(currentUserId);
    const actionEvents = useActionEvents();
    // default to me
    if (targetType === PostTargetType.GlobalFeed || targetType === PostTargetType.MyFeed) {
        /* eslint-disable no-param-reassign */
        targetType = PostTargetType.UserFeed;
        /* eslint-disable no-param-reassign */
        targetId = currentUserId;
    }
    const [target, setTarget] = useState({ targetType, targetId });
    useEffect(() => {
        setTarget({ targetType, targetId });
    }, [targetType, targetId]);
    const fetcher = target.targetType === PostTargetType.UserFeed ? userFetcher : communityFetcher;
    const model = useLiveObject(fetcher(target.targetId), [target.targetId]);
    const fileUrl = useImage({ fileId: model.avatarFileId });
    const [postText, setPostText] = useState('');
    const [plainText, setPlainText] = useState('');
    const [postImages, setPostImages] = useState([]);
    const [postVideos, setPostVideos] = useState([]);
    const [postFiles, setPostFiles] = useState([]);
    // Images/files incoming from uploads.
    const [incomingImages, setIncomingImages] = useState([]);
    const [incomingVideos, setIncomingVideos] = useState([]);
    const [incomingFiles, setIncomingFiles] = useState([]);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [setError] = useErrorNotification();
    const [mentionees, setMentionees] = useState([]);
    const [onCreatePost, creating] = useAsyncCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        const data = {};
        const attachments = [];
        const postMentionees = {};
        const metadata = {};
        if (postText) {
            data.text = plainText;
        }
        if (postImages.length) {
            attachments.push(...postImages.map((i) => ({ fileId: i.fileId, type: FileType.Image })));
        }
        if (postVideos.length) {
            attachments.push(...postVideos.map((i) => ({ fileId: i.fileId, type: FileType.Video })));
        }
        if (postFiles.length) {
            attachments.push(...postFiles.map((i) => ({ fileId: i.fileId, type: FileType.File })));
        }
        if (mentionees.length) {
            postMentionees.type = 'user';
            postMentionees.userIds = mentionees.map(({ id }) => id);
        }
        if (((_c = data.text) === null || _c === void 0 ? void 0 : _c.length) > MAXIMUM_POST_CHARACTERS) {
            overCharacterModal();
            return;
        }
        const createPostParams = Object.assign(Object.assign({}, target), { data,
            attachments, metadata: {} });
        if (postMentionees.type && postMentionees.userIds.length > 0) {
            createPostParams.mentionees = [Object.assign({}, postMentionees)];
            const { metadata: extractedMetadata } = extractMetadata(mentionees);
            metadata.markupText = postText;
            createPostParams.metadata = Object.assign(Object.assign({}, metadata), extractedMetadata);
        }
        const post = yield createPost(createPostParams);
        onCreateSuccess(post.postId);
        setPostText('');
        setPostImages([]);
        setPostVideos([]);
        setPostFiles([]);
        setIncomingImages([]);
        setIncomingVideos([]);
        setIncomingFiles([]);
        setMentionees([]);
        showPostCreatedNotification(post, model);
        (_d = actionEvents.onPostCreate) === null || _d === void 0 ? void 0 : _d.call(actionEvents, { postId: post.postId, source: targetType });
    }), [postText, postImages, postVideos, postFiles, target, onCreateSuccess, model, mentionees]);
    const onMaxFilesLimit = () => {
        notification.info({
            content: React.createElement(FormattedMessage, { id: "upload.attachmentLimit", values: { maxFiles } }),
        });
    };
    const onFileSizeLimit = () => {
        notification.info({
            content: React.createElement(FormattedMessage, { id: "upload.fileSizeLimit" }),
        });
    };
    const isCommunity = target.targetType === PostTargetType.CommunityFeed;
    const CurrentTargetAvatar = React.createElement(Avatar, { isCommunity: isCommunity, avatar: fileUrl });
    const isDisabled = isEmpty(postText, postImages, postVideos, postFiles) || uploadLoading || creating || !connected;
    const hasChanges = !isEmpty(postText, postImages, postVideos, postFiles);
    const showAttachments = uploadLoading || !isEmpty(postImages, postVideos, postFiles);
    useEffect(() => {
        if (hasChanges) {
            setNavigationBlocker({
                title: React.createElement(FormattedMessage, { id: "post.discard.title" }),
                content: React.createElement(FormattedMessage, { id: "post.discard.content" }),
                okText: React.createElement(FormattedMessage, { id: "general.action.discard" }),
            });
        }
        else {
            setNavigationBlocker(null);
        }
    }, [hasChanges, setNavigationBlocker]);
    const [isPollModalOpened, setPollModalOpened] = useState(false);
    const openPollModal = () => setPollModalOpened(true);
    const [mentionText, setMentionText] = useState();
    const creatorTargetType = (_a = target === null || target === void 0 ? void 0 : target.targetType) !== null && _a !== void 0 ? _a : targetType;
    const creatorTargetId = (_b = target === null || target === void 0 ? void 0 : target.targetId) !== null && _b !== void 0 ? _b : targetId;
    const queryMentionees = useCallback((query, cb) => __awaiter(void 0, void 0, void 0, function* () {
        let keyword = query || mentionText;
        let users;
        // Weird hack to show users to show users on start
        if (keyword.match(/^@$/)) {
            return;
        }
        // Only fetch private community members
        if (creatorTargetType === PostTargetType.CommunityFeed && !(model === null || model === void 0 ? void 0 : model.isPublic)) {
            const liveCollection = CommunityRepository.getCommunityMembers({
                communityId: creatorTargetId,
                search: keyword,
            });
            const communityMembers = yield promisify(liveCollection);
            users = communityMembers.map((member) => member.user);
        }
        else {
            const liveCollection = UserRepository.queryUsers({ keyword });
            users = yield promisify(liveCollection);
        }
        cb(formatMentionees(users));
    }), [mentionText, model === null || model === void 0 ? void 0 : model.isPublic, creatorTargetId, creatorTargetType]);
    return (React.createElement(PostCreatorContainer, { className: cx('postComposeBar', className) },
        isPollModalOpened && (React.createElement(PollModal, { targetId: creatorTargetId, targetType: creatorTargetType, onCreatePoll: (pollId, text, pollMentionees, metadata) => createPost({
                targetId: creatorTargetId,
                targetType: creatorTargetType,
                data: { pollId, text },
                dataType: 'poll',
                mentionees: pollMentionees,
                metadata,
            }), onClose: () => setPollModalOpened(false) })),
        enablePostTargetPicker ? (React.createElement(PostTargetSelector, { user: user, communities: communities, canTargetUser: canTargetUser, hasMoreCommunities: hasMoreCommunities, loadMoreCommunities: loadMoreCommunities, currentTargetType: target.targetType, currentTargetId: target.targetId, onChange: setTarget }, CurrentTargetAvatar)) : (CurrentTargetAvatar),
        React.createElement(PostContainer, null,
            React.createElement(Editor, { id: "create-post", "data-qa-anchor": "post-creator-textarea", multiline: true, value: postText, placeholder: formatMessage({ id: 'whatsGoingOnPlaceholder' }), mentionAllowed: true, queryMentionees: queryMentionees, loadMoreMentionees: () => queryMentionees(mentionText), 
                // Need to work on this, possible conflict incoming
                append: React.createElement(UploadsContainer, { show: showAttachments },
                    React.createElement(ImagesUploaded, { files: incomingImages, uploadLoading: uploadLoading, onLoadingChange: setUploadLoading, onChange: setPostImages, onError: setError }),
                    React.createElement(VideosUploaded, { files: incomingVideos, uploadLoading: uploadLoading, onLoadingChange: setUploadLoading, onChange: setPostVideos, onError: setError }),
                    React.createElement(FilesUploaded, { files: incomingFiles, uploadLoading: uploadLoading, onLoadingChange: setUploadLoading, onChange: setPostFiles, onError: setError })), onChange: ({ text, plainText: plainTextVal, lastMentionText, mentions }) => {
                    // Disrupt the flow
                    if ((mentions === null || mentions === void 0 ? void 0 : mentions.length) > MAXIMUM_POST_MENTIONEES) {
                        return info({
                            title: React.createElement(FormattedMessage, { id: "postCreator.unableToMention" }),
                            content: React.createElement(FormattedMessage, { id: "postCreator.overMentionees" }),
                            okText: React.createElement(FormattedMessage, { id: "postCreator.okText" }),
                            type: 'info',
                        });
                    }
                    setMentionees(mentions);
                    setMentionText(lastMentionText);
                    setPostText(text);
                    setPlainText(plainTextVal);
                } }),
            React.createElement(Footer, { "data-qa-anchor": "post-creator-footer" },
                React.createElement(UploaderButtons, { imageUploadDisabled: postFiles.length > 0 || postVideos.length > 0 || uploadLoading, videoUploadDisabled: postFiles.length > 0 || postImages.length > 0 || uploadLoading, fileUploadDisabled: postImages.length > 0 || postVideos.length > 0 || uploadLoading, fileLimitRemaining: maxFiles - postFiles.length - postImages.length - postVideos.length, uploadLoading: uploadLoading, onChangeImages: setIncomingImages, onChangeVideos: setIncomingVideos, onChangeFiles: setIncomingFiles, onMaxFilesLimit: onMaxFilesLimit, onFileSizeLimit: onFileSizeLimit }),
                React.createElement(PollButton, { "data-qa-anchor": "post-creator-poll-button", onClick: openPollModal },
                    React.createElement(FileLoaderContainer, null,
                        React.createElement(PollIcon, null))),
                React.createElement(PostButton, { disabled: isDisabled, "data-qa-anchor": "post-creator-post-button", onClick: onCreatePost },
                    React.createElement(FormattedMessage, { id: "post" })),
                enablePostTargetPicker ? React.createElement(PostTargetInfo, { postTarget: model === null || model === void 0 ? void 0 : model.displayName }) : null))));
};
PostCreatorBar.propTypes = {
    canTargetUser: PropTypes.bool,
    currentUserId: PropTypes.string,
    targetType: PropTypes.string,
    targetId: PropTypes.string,
    communities: PropTypes.array,
    className: PropTypes.string,
    placeholder: PropTypes.string,
    hasMoreCommunities: PropTypes.bool,
    loadMoreCommunities: PropTypes.func,
    enablePostTargetPicker: PropTypes.bool,
    maxFiles: PropTypes.number,
    connected: PropTypes.bool,
    onCreateSuccess: PropTypes.func,
};
export default memo(withSDK(PostCreatorBar));
//# sourceMappingURL=index.js.map